import classNames from 'classnames';

// Design System
import { UnderlinedButton } from 'jl-design-system/components/button/Button';

// Types
import { AddressProps } from 'types/Address.types';
import { AddressKey, DisplayAddressProps } from './DisplayAddress.types';

// Components
import BodyText from '@components/body-text';
import Heading from '@components/heading';
import SaveAsDefaultForm from '@components/save-as-default-form/SaveAsDefaultForm';
import ClickAndCollectStoreIcon from '@components/click-and-collect-store-icon';

import styles from './display-address.scss';

const DisplayAddress = ({
  address,
  addressee = {},
  displayAddressTitle,
  phoneNumber,
  inline,
  inlineAddressOnly,
  isClickCollectOrder,
  renderPostcodeLast,
  collectionAddressTitle,
  openMoreDetailsModal,
  hideMoreDetailsLink,
  highlightName,
  addressTitle,
  'data-testid': dataTest,
  deferSaveAsDefaultForm,
  saveAsDefaultObject,
  saveAsDefaultType,
  disabled,
  isUndeliverableAddress,
  isSinglePageCheckout,
  displayStoreNameInline,
  selectedCollectionPoint,
  saveAddressError,
  isContactAddress,
  isPaymentAddress,
  isBillingAddress,
}: DisplayAddressProps) => {
  const renderAddressItem = (address: AddressProps, key: AddressKey, showComma: string) => {
    const addressItemId = {
      addressLine1: 'address-line-1',
      addressLine2: 'address-line-2',
      addressLine3: 'address-line-3',
      addressLine4: 'address-line-4',
      townOrCity: 'town-or-city',
      countyStateOrProvince: 'county-state-or-province',
      countryCode: 'country-code',
      postcode: 'postcode',
    };

    return (
      <BodyText
        key={key}
        maskText={key !== 'townOrCity' && key !== 'postcode'}
        noTheme
        tag="span"
        testId={`address-${addressItemId[key] ?? key}`}
      >
        {`${address[key]}${showComma}`}
      </BodyText>
    );
  };

  const transformAddress = () => {
    const { addressLine1 = '', addressLine2 } = address ?? {};

    const startsWithNumber = addressLine1.match(/^[0-9]+$/);

    return {
      storeName: displayStoreNameInline ? collectionAddressTitle : null,
      ...address,
      addressLine1: startsWithNumber && addressLine2 ? `${addressLine1} ${addressLine2}` : addressLine1,
      addressLine2: startsWithNumber ? '' : addressLine2,
    };
  };

  const getAddressType = () => {
    if (isContactAddress) {
      return (
        <span className={styles.addressType} data-testid="display-address-type-contact">
          (Contact address)
        </span>
      );
    }

    if (isBillingAddress) {
      return (
        <span className={styles.addressType} data-testid="display-address-type-payment">
          (Billing address)
        </span>
      );
    }

    if (isPaymentAddress) {
      return (
        <span className={styles.addressType} data-testid="display-address-type-payment">
          (Payment address)
        </span>
      );
    }

    return null;
  };

  const transformedAddress = transformAddress();
  const titleClassName = inline ? styles.inlineTitleArea : styles.titleArea;
  const addressClassName = inline
    ? styles.inlineAddress
    : isClickCollectOrder && isSinglePageCheckout
      ? classNames(
          styles.address,
          styles.inlineAddressOnly,
          styles.addressWithIcon,
          styles.singlePageCollectionPointAddress,
        )
      : inlineAddressOnly
        ? styles.inlineAddressOnly
        : classNames(styles.address, {
            [styles.addressWithIcon]: isClickCollectOrder,
          });
  const comma = inline ? ',' : '';
  const addressKeys = Object.keys(transformedAddress).filter(
    (key) => !(isClickCollectOrder && isSinglePageCheckout && key === 'storeName'),
  ) as AddressKey[];

  const countyKeyExists = !!transformedAddress?.countyStateOrProvince;
  if (renderPostcodeLast || countyKeyExists) {
    addressKeys.push(addressKeys.splice(addressKeys.indexOf('postcode'), 1)[0]);
  }

  const title = isClickCollectOrder ? collectionAddressTitle : displayAddressTitle;
  const addresseeTitle = addressee?.title ? `${addressee.title} ` : '';

  return (
    <div
      className={classNames(styles.container, {
        [styles.disabled]: disabled,
      })}
      data-testid={dataTest}
    >
      {getAddressType()}

      {title && !displayStoreNameInline && !isSinglePageCheckout && (
        <div className={titleClassName}>
          <Heading label={title} marginBottom="1" tag="h3" testId="display-address-title" type="xs" />
        </div>
      )}

      <div className={addressClassName}>
        {isClickCollectOrder && <ClickAndCollectStoreIcon store={selectedCollectionPoint} />}

        <>
          {isClickCollectOrder && isSinglePageCheckout && selectedCollectionPoint?.storeName && (
            <Heading
              label={selectedCollectionPoint.storeName}
              marginBottom="0.5"
              tag="h5"
              testId="display-address-store-name"
              type="xs"
            />
          )}

          {addressTitle && (
            <Heading label={addressTitle} marginBottom="2" tag="h3" testId="display-address-address-title" type="xs" />
          )}
          {!isClickCollectOrder && (addressee.title || addressee.firstName || addressee.lastName) && (
            <BodyText
              className={classNames({
                [styles.block]: inlineAddressOnly,
                [styles.addresseeStong]: inlineAddressOnly,
              })}
              maskText
              noTheme
              tag={highlightName ? 'strong' : 'span'}
              testId="addressee"
            >
              {`${addresseeTitle}${addressee.firstName} ${addressee.lastName}${comma}`}
            </BodyText>
          )}
          {(() => {
            // These comma helper functions determine comma location for inline/single page checkout addresses
            const hasAnyNextValue = (index: number) =>
              addressKeys.slice(index + 1).some((nextKey) => !!transformedAddress[nextKey]);

            const hasNextVisibleValue = (index: number) =>
              addressKeys
                .slice(index + 1)
                .some(
                  (nextKey) =>
                    !!transformedAddress[nextKey] &&
                    !(nextKey === 'countryCode' && transformedAddress.countryCode === 'GB'),
                );

            return addressKeys.map((key: AddressKey, i) => {
              const showComma = inline
                ? hasAnyNextValue(i)
                  ? ','
                  : ''
                : isSinglePageCheckout
                  ? hasNextVisibleValue(i)
                    ? ','
                    : ''
                  : '';

              if (!transformedAddress[key] || key === 'countryCode') {
                return '';
              }

              return renderAddressItem(transformedAddress, key, showComma);
            });
          })()}
          {transformedAddress.countryCode &&
            transformedAddress.countryCode !== 'GB' &&
            renderAddressItem(transformedAddress, 'countryCode', comma)}
          {!isClickCollectOrder && phoneNumber && (
            <BodyText
              className={classNames(styles.phoneNumber, {
                [styles.block]: inlineAddressOnly,
              })}
              maskText
              noTheme
              tag="span"
              testId="address-phone"
            >
              {phoneNumber}
            </BodyText>
          )}
        </>
      </div>
      {isClickCollectOrder && !hideMoreDetailsLink && (
        <UnderlinedButton
          className={classNames(styles.moreDetailsButton, {
            [styles.singlePageCollectionPointMoreDetails]: isSinglePageCheckout,
          })}
          data-testid="more-details-button"
          labelText="Map and opening times"
          onClick={openMoreDetailsModal}
        />
      )}
      {!deferSaveAsDefaultForm && !isUndeliverableAddress && !saveAddressError && (
        <SaveAsDefaultForm saveAsDefaultObject={saveAsDefaultObject} type={saveAsDefaultType} />
      )}
    </div>
  );
};

export default DisplayAddress;
